import { Fragment } from 'react';
import styled from 'styled-components';
import {
  useDeleteGeoSheetMutation,
  useSetGeoSheetPageMutation,
  useUploadGeoSheetMutation,
} from '@shared/api';
import { fmtNumber, fmtScale } from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { Select } from '@shared/ui/Input';
import {
  ErrorText,
  Facts,
  FieldLabel,
  Hint,
  Muted,
  Notice,
  Panel,
  PanelHead,
  PanelTitle,
  Stack,
  useAction,
} from '@widgets/GeoSchemes';
import { RdSheetPicker, revisionName } from './RdSheet';
import { SheetSetPanel } from './SheetSetPanel';
import { StepShell, useWizard } from './wizard';
import { unitsLabel, useSheetPreviewUrl } from './sheetPreview';

const Split = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(260px, 1fr);
  gap: ${({ theme }) => theme.spacing(4)};
  align-items: start;

  @container geo-wizard (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewFrame = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};

  img {
    display: block;
    max-width: 100%;
    max-height: 640px;
    object-fit: contain;
    background: ${({ theme }) => theme.colors.backgroundElevated};
    box-shadow: ${({ theme }) => theme.shadow.sm};
  }
`;

const PageField = styled(FieldLabel)`
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const WarnList = styled.ul`
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing(5)};
`;

const ACCEPT_SHEET = '.pdf,.dxf';
/** Графы основной надписи листа РД (ГОСТ Р 21.101, форма 3), как их читает осмотр. */
const RD_STAMP_LABELS: Record<string, string> = {
  designation: 'Обозначение (графа 1)',
  object_name: 'Объект (графа 2)',
  building: 'Здание, сооружение (графа 3)',
  sheet_title: 'Изображение (графа 5)',
  stage: 'Стадия (графа 6)',
  sheet_no: 'Лист (графа 7)',
  sheets_total: 'Листов (графа 8)',
  organization: 'Организация (графа 9)',
};
const PT_TO_MM = 25.4 / 72;

/** Шаг 1. Проектный лист — подложка РД (PDF или DXF) и её осмотр. */
export function StepSheet() {
  const { scheme, readOnly } = useWizard();
  const sheet = scheme.sheet;
  const [upload] = useUploadGeoSheetMutation();
  const [setPage] = useSetGeoSheetPageMutation();
  const [removeSheet] = useDeleteGeoSheetMutation();
  const uploading = useAction();
  const paging = useAction();
  const removing = useAction();
  const preview = useSheetPreviewUrl(scheme);

  function onFile(file: File) {
    void uploading.run(
      () => upload({ id: scheme.id, file }).unwrap(),
      'Лист не загрузился. Проверьте формат: PDF или DXF.',
    );
  }

  function dropSheet() {
    if (
      !window.confirm(
        'Работать без подложки? Проектный лист будет убран из схемы, привязка по точкам листа перестанет действовать.',
      )
    )
      return;
    void removing.run(() => removeSheet(scheme.id).unwrap(), 'Лист не удалось убрать.');
  }

  const dropZone = (compact: boolean) => (
    <FileDropZone
      accept={ACCEPT_SHEET}
      compact={compact}
      disabled={uploading.busy}
      primary={
        uploading.busy
          ? 'Загружаем и осматриваем лист…'
          : compact
            ? 'Заменить лист: PDF или DXF'
            : 'Перетащите проектный лист — PDF или DXF'
      }
      hint="или нажмите, чтобы выбрать файл"
      onFile={onFile}
    />
  );

  const stampEntries = sheet ? Object.entries(sheet.stamp).filter(([, v]) => v.trim() !== '') : [];

  return (
    <StepShell
      n={1}
      lead="Лист рабочей документации — основа схемы (ГОСТ Р 51872 п.5.6 допускает рабочий чертёж как подложку). Можно и без подложки: тогда схема строится по каталогу координат."
    >
      {!sheet ? (
        <Panel>
          <Stack $gap={4}>
            <RdSheetPicker />
            {readOnly ? <Muted>Проектный лист не загружен.</Muted> : dropZone(false)}
            {uploading.error && <ErrorText role="alert">{uploading.error}</ErrorText>}
            <Hint>
              DWG сохраните из CAD как DXF: «Файл → Сохранить как → DXF» (AutoCAD, nanoCAD,
              BricsCAD). Для PDF берётся векторная страница листа.
            </Hint>
            <Hint>
              Без подложки: пропустите шаг — на шаге 2 задайте угол осей здания, на шаге 6 загрузите
              каталог координат проектных точек.
            </Hint>
          </Stack>
        </Panel>
      ) : (
        <Split>
          <Panel aria-label="Превью проектного листа">
            <PreviewFrame>
              {preview.url ? (
                <img src={preview.url} alt={`Проектный лист ${sheet.file_name}`} />
              ) : preview.isError ? (
                <ErrorText>Превью листа не загрузилось.</ErrorText>
              ) : (
                <Muted>Загрузка превью…</Muted>
              )}
            </PreviewFrame>
          </Panel>

          <Stack $gap={4}>
            <Panel>
              <PanelHead>
                <PanelTitle>Сведения о листе</PanelTitle>
              </PanelHead>
              <Facts>
                <dt>Файл</dt>
                <dd>{sheet.file_name}</dd>
                <dt>Реестр РД ПТО</dt>
                <dd>
                  {sheet.rd
                    ? `${sheet.rd.code} «${sheet.rd.title}», ${revisionName(sheet.rd.rev_label, sheet.rd.revision_no)}`
                    : 'не из реестра'}
                </dd>
                <dt>Формат</dt>
                <dd>{sheet.kind === 'pdf' ? 'PDF' : 'DXF'}</dd>
                <dt>Размер</dt>
                <dd>
                  {fmtNumber(sheet.width, 1)} × {fmtNumber(sheet.height, 1)}{' '}
                  {unitsLabel(sheet.units)}
                  {sheet.units.toLowerCase() === 'pt' && (
                    <>
                      {' '}
                      ≈ {Math.round(sheet.width * PT_TO_MM)} × {Math.round(sheet.height * PT_TO_MM)}{' '}
                      мм
                    </>
                  )}
                </dd>
                <dt>Масштаб РД</dt>
                <dd>
                  {sheet.scale_hint
                    ? fmtScale(sheet.scale_hint)
                    : 'не распознан — укажите на шаге 3'}
                </dd>
                {sheet.kind === 'dxf' && (
                  <>
                    <dt>Слои / блоки</dt>
                    <dd>
                      {sheet.layers.length} / {sheet.blocks.length}
                    </dd>
                  </>
                )}
              </Facts>
              {sheet.pages > 1 && (
                <PageField>
                  Страница с планом
                  <Select
                    value={sheet.page}
                    disabled={readOnly || paging.busy}
                    onChange={(e) =>
                      void paging.run(
                        () => setPage({ id: scheme.id, page: Number(e.target.value) }).unwrap(),
                        'Страницу не удалось переключить.',
                      )
                    }
                  >
                    {Array.from({ length: sheet.pages }, (_, i) => (
                      <option key={i} value={i}>
                        Страница {i + 1} из {sheet.pages}
                      </option>
                    ))}
                  </Select>
                </PageField>
              )}
              {paging.error && <ErrorText role="alert">{paging.error}</ErrorText>}
            </Panel>

            {stampEntries.length > 0 && (
              <Panel>
                <PanelHead>
                  <PanelTitle>Штамп РД</PanelTitle>
                </PanelHead>
                <Facts>
                  {stampEntries.map(([k, v]) => (
                    <Fragment key={k}>
                      <dt>{RD_STAMP_LABELS[k] ?? k}</dt>
                      <dd>{v}</dd>
                    </Fragment>
                  ))}
                </Facts>
              </Panel>
            )}

            {sheet.warnings.length > 0 && (
              <Notice $tone="warning" role="status">
                <strong>Осмотр листа</strong>
                <WarnList>
                  {sheet.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </WarnList>
              </Notice>
            )}

            {!readOnly && (
              <Panel>
                <Stack $gap={3}>
                  <RdSheetPicker />
                  {dropZone(true)}
                  {uploading.error && <ErrorText role="alert">{uploading.error}</ErrorText>}
                  <div>
                    <Button
                      type="button"
                      $variant="ghost"
                      $size="sm"
                      onClick={dropSheet}
                      disabled={removing.busy}
                    >
                      {removing.busy ? 'Убираем…' : 'Работать без подложки'}
                    </Button>
                  </div>
                  {removing.error && <ErrorText role="alert">{removing.error}</ErrorText>}
                </Stack>
              </Panel>
            )}
          </Stack>
        </Split>
      )}
      {/* Листы комплекта — на всю ширину: таблица листов с ролями и страницами. */}
      {sheet && <SheetSetPanel />}
    </StepShell>
  );
}
